import { useState, useEffect, useMemo } from "react";
import PlayerCard from "./PlayerCard";
import type { AdminTeam as Team, AdminPlayer as Player } from "../types/admin";
import { API_BASE_URL, authenticatedFetch } from "../utils/api";
import { PlayerRole } from "../shared/constants";

interface TeamCardProps {
  team: Team;
  gameStatus?: string;
  refreshKey?: number;
  playersData?: Player[];
  currentTurn?: number;
  playerActionCounts?: Record<string, number>;
  playerCharCounts?: Record<string, number>;
  playerLandingSiteInvestments?: Record<string, { total_points: number; investments: Array<{player_id: string; player_name: string; team_name: string; count: number}> }>;
}

export default function TeamCard({ team, gameStatus, refreshKey, playersData, currentTurn, playerActionCounts, playerCharCounts, playerLandingSiteInvestments }: TeamCardProps) {
  const [players, setPlayers] = useState<Player[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [error, setError] = useState<string>("");
  
  // Find spaceship player
  const spaceshipPlayer = useMemo(() => {
    const spaceship = players.find(p => p.role === PlayerRole.SPACESHIP);
    if (spaceship) {
      console.log(`[TeamCard] Spaceship player found: ${spaceship.player_id}, current_location:`, spaceship.current_location);
    }
    return spaceship;
  }, [players]);
  
  const [locationValue, setLocationValue] = useState<string>("");
  const [isEditingLocation, setIsEditingLocation] = useState<boolean>(false);
  const [isSavingLocation, setIsSavingLocation] = useState<boolean>(false);

  useEffect(() => {
    // Always use playersData if provided - no individual fetch needed
    if (playersData) {
      setPlayers(playersData);
      setIsLoading(false);
      setError("");
      return;
    }

    // Only fetch individually if playersData is not provided (fallback for standalone usage)
    // This should rarely happen if ViewTeams is used correctly
    let cancelled = false;
    setIsLoading(true);
    setError("");
    fetch(`${API_BASE_URL}/players/by-team-id?team_id=${team.team_id}`)
      .then(res => {
        if (!res.ok) {
          // If 404, treat as no players found (empty array)
          if (res.status === 404) {
            return [];
          }
          return res.json().then(err => {
            throw new Error(err.detail || "Failed to fetch players");
          });
        }
        return res.json();
      })
      .then((data: Player[]) => {
        if (cancelled) return;
        setPlayers(data);
        setIsLoading(false);
      })
      .catch((err) => {
        if (cancelled) return;
        setError(err.message || "Failed to fetch players");
        setIsLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [playersData, team.team_id, refreshKey]);

  // Update location value when spaceship player changes
  useEffect(() => {
    if (spaceshipPlayer) {
      setLocationValue(spaceshipPlayer.current_location || "");
    }
  }, [spaceshipPlayer]);

  const handleLocationChange = async () => {
    if (!spaceshipPlayer) return;
    
    // Find the agency player for this team
    const agencyPlayer = players.find(p => p.role === PlayerRole.AGENCY);
    if (!agencyPlayer) {
      alert("Agency player not found for this team");
      return;
    }
    
    setIsSavingLocation(true);
    try {
      const response = await authenticatedFetch("/players/location/update", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          player_id: spaceshipPlayer.player_id,
          game_id: team.game_id,
          agency_player_id: agencyPlayer.player_id,
          current_location: locationValue || null
        })
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || "Failed to update location");
      }

      const updatedPlayer: Player = await response.json();
      // Update local players state
      setPlayers(prev => prev.map(p => 
        p.player_id === updatedPlayer.player_id ? updatedPlayer : p
      ));
      setIsEditingLocation(false);
    } catch (err) {
      console.error("Error updating location:", err);
      alert(err instanceof Error ? err.message : "Failed to update location");
    } finally {
      setIsSavingLocation(false);
    }
  };

  // Sort players so agency is always on the left and spaceship is on the right
  const sortedPlayers = useMemo(() => {
    const sorted = [...players];
    sorted.sort((a, b) => {
      // Agency comes first (left), Spaceship comes second (right)
      const aIsAgency = a.role === PlayerRole.AGENCY;
      const bIsAgency = b.role === PlayerRole.AGENCY;
      const aIsSpaceship = a.role === PlayerRole.SPACESHIP;
      const bIsSpaceship = b.role === PlayerRole.SPACESHIP;
      
      // Agency always comes before Spaceship
      if (aIsAgency && bIsSpaceship) {
        return -1; // Agency before Spaceship
      }
      if (aIsSpaceship && bIsAgency) {
        return 1; // Spaceship after Agency
      }
      // If same role or unknown, maintain original order
      return 0;
    });
    return sorted;
  }, [players]);

  return (
    <div style={{
      padding: "0.25rem 1rem",
      backgroundColor: "#f8f9fa",
      borderRadius: "8px",
      border: "1px solid #dee2e6",
      boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
      marginBottom: "1rem"
    }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginTop: "0", marginBottom: "0", gap: "1rem", flexWrap: "wrap" }}>
        <h4 style={{ margin: 0, color: "#212529" }}>{team.team_name}</h4>
        <div style={{ display: "flex", gap: "0.5rem", alignItems: "center", flexWrap: "wrap", flex: 1, justifyContent: "center" }}>
          {spaceshipPlayer && (
            <>
              {isEditingLocation ? (
                <div style={{ display: "flex", gap: "0.5rem", alignItems: "center", flexWrap: "wrap" }}>
                  <span style={{ fontSize: "0.875rem", fontWeight: "500" }}>Spaceship Location:</span>
                  <input
                    type="text"
                    value={locationValue}
                    onChange={(e) => setLocationValue(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter") {
                        handleLocationChange();
                      } else if (e.key === "Escape") {
                        setLocationValue(spaceshipPlayer.current_location || "");
                        setIsEditingLocation(false);
                      }
                    }}
                    style={{
                      padding: "0.5rem",
                      fontSize: "0.875rem",
                      border: "1px solid #ced4da",
                      borderRadius: "4px",
                      minWidth: "150px"
                    }}
                    autoFocus
                    disabled={isSavingLocation}
                  />
                  <button
                    onClick={handleLocationChange}
                    disabled={isSavingLocation}
                    style={{
                      padding: "0.5rem 1rem",
                      fontSize: "0.875rem",
                      cursor: isSavingLocation ? "not-allowed" : "pointer",
                      backgroundColor: "#28a745",
                      color: "white",
                      border: "none",
                      borderRadius: "4px",
                      opacity: isSavingLocation ? 0.6 : 1
                    }}
                  >
                    {isSavingLocation ? "Saving..." : "Save"}
                  </button>
                  <button
                    onClick={() => {
                      setIsEditingLocation(false);
                      setLocationValue(spaceshipPlayer.current_location || "");
                    }}
                    disabled={isSavingLocation}
                    style={{
                      padding: "0.5rem 1rem",
                      fontSize: "0.875rem",
                      cursor: isSavingLocation ? "not-allowed" : "pointer",
                      backgroundColor: "#6c757d",
                      color: "white",
                      border: "none",
                      borderRadius: "4px",
                      opacity: isSavingLocation ? 0.6 : 1
                    }}
                  >
                    Cancel
                  </button>
                </div>
              ) : (
                <div style={{ display: "flex", alignItems: "center", gap: "0.5rem" }}>
                  <span style={{ fontSize: "0.875rem", fontWeight: "500" }}>Spaceship Location:</span>
                  <span style={{ color: "#212529", fontSize: "0.875rem" }}>
                    {spaceshipPlayer.current_location != null && spaceshipPlayer.current_location !== "" 
                      ? spaceshipPlayer.current_location 
                      : <em style={{ color: "#6c757d" }}>No location set</em>}
                  </span>
                  <button
                    onClick={() => setIsEditingLocation(true)}
                    style={{
                      padding: "0.25rem 0.5rem",
                      fontSize: "0.75rem",
                      cursor: "pointer",
                      backgroundColor: "#007bff",
                      color: "white",
                      border: "none",
                      borderRadius: "4px"
                    }}
                  >
                    Edit
                  </button>
                </div>
              )}
            </>
          )}
        </div>
        <div style={{ display: "flex", gap: "0.5rem", alignItems: "center", flexWrap: "wrap" }}>
          <button
            onClick={async (e) => {
              try {
                await navigator.clipboard.writeText(team.team_id);
                // Show a brief success message
                const button = e.currentTarget;
                const originalText = button.textContent;
                button.textContent = "Copied!";
                setTimeout(() => {
                  button.textContent = originalText;
                }, 2000);
              } catch (err) {
                console.error("Failed to copy team ID:", err);
                alert(`Team ID: ${team.team_id}`);
              }
            }}
            style={{
              padding: "0.5rem 1rem",
              cursor: "pointer",
              backgroundColor: "rgba(23, 162, 184, 0.1)",
              color: "#17a2b8",
              border: "1px solid rgba(23, 162, 184, 0.3)",
              borderRadius: "4px",
              fontSize: "0.875rem",
              fontWeight: "bold",
              display: "flex",
              alignItems: "center",
              justifyContent: "center"
            }}
            title="Copy Team ID to clipboard"
          >
            Copy Team ID
          </button>
        </div>
      </div>

      <div style={{ marginTop: "0.25rem" }}>
        {isLoading && (
          <div style={{ padding: "0.5rem", color: "#6c757d", fontSize: "0.85rem" }}>Loading players...</div>
        )}
        {error && (
          <div style={{ padding: "0.5rem", color: "#dc3545", fontSize: "0.85rem" }}>{error}</div>
        )}
        {!isLoading && !error && players.length === 0 && (
          <div style={{ padding: "0.5rem", color: "#6c757d", fontSize: "0.85rem" }}>No players found</div>
        )}
        {!isLoading && !error && sortedPlayers.length > 0 && (
          <div style={{ display: "flex", gap: "0.75rem", flexWrap: "wrap" }}>
            {sortedPlayers.map((player) => (
              <div key={player.player_id} style={{ flex: "1", minWidth: "200px" }}>
                <PlayerCard 
                  player={player} 
                  gameStatus={gameStatus} 
                  currentTurn={currentTurn}
                  actionCount={playerActionCounts?.[player.player_id]}
                  charsSentThisTurn={playerCharCounts?.[player.player_id]}
                  landingSiteInvestmentData={playerLandingSiteInvestments?.[player.player_id]}
                />
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

